import { Casino } from '../../mocks/casinos';

interface CasinoReviewPanelProps {
  casino: Casino;
  isOpen: boolean;
  onClose: () => void;
}

const paymentMethodLogoMap: Record<string, string> = {
  Visa: '/payment-method-logos/visa.png',
  Mastercard: '/payment-method-logos/mastercard.png',
  'Apple Pay': '/payment-method-logos/applepay.png',
  'Google Pay': '/payment-method-logos/googlepay.png',
  PayID: '/payment-method-logos/payid.png',
  Osko: '/payment-method-logos/osko.png',
  Amopay: '/payment-method-logos/amopay.png',
};

const licenseLogoMap: Record<string, string> = {
  'Curacao Gaming Authority (CGA)': '/license-logos/cga.png',
  PAGCOR: '/license-logos/pagcor.png',
};

export default function CasinoReviewPanel({ casino, isOpen, onClose }: CasinoReviewPanelProps) {
  if (!isOpen) return null;

  return (
    <div className="bg-gray-50 border-t border-gray-200 p-4 sm:p-8 animate-fadeIn">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-6 sm:mb-8">
          <div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
              {casino.name} Review
            </h3>
            <p className="text-gray-600">Expert analysis and detailed breakdown</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-200 rounded-lg transition-colors"
          >
            <i className="ri-close-line text-xl text-gray-600"></i>
          </button>
        </div>

        {/* Trust Score Breakdown */}
        {casino.trustScore && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 mb-6">
            <h4 className="text-lg font-bold text-gray-900 mb-4">Trust Score Breakdown</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {Object.entries(casino.trustScore).map(([key, value]) => (
                <div key={key}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-gray-700 capitalize">{key}</span>
                    <span className="text-sm font-bold text-gray-900">{value}/100</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full transition-all"
                      style={{ width: `${value}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {/* Pros & Cons */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-thumb-up-line text-green-600"></i>
              Pros
            </h4>
            <ul className="space-y-2">
              {casino.pros?.map((pro, index) => (
                <li key={index} className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 text-sm mt-1 flex-shrink-0"></i>
                  <span className="text-sm text-gray-700">{pro}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-thumb-down-line text-red-600"></i>
              Cons
            </h4>
            <ul className="space-y-2">
              {casino.cons?.map((con, index) => (
                <li key={index} className="flex items-start gap-2">
                  <i className="ri-close-line text-red-600 text-sm mt-1 flex-shrink-0"></i>
                  <span className="text-sm text-gray-700">{con}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Game Library Breakdown */}
        {casino.gameLibrary && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 mt-6">
            <h4 className="text-lg font-bold text-gray-900 mb-4">Game Library</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-purple-600 mb-1">
                  {casino.gameLibrary.totalGames ?? casino.games}
                </div>
                <div className="text-xs text-gray-600">Total Games</div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-purple-600 mb-1">
                  {casino.gameLibrary.providers ?? '50+'}
                </div>
                <div className="text-xs text-gray-600">Providers</div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-purple-600 mb-1">
                  {casino.gameLibrary.exclusives ?? '20+'}
                </div>
                <div className="text-xs text-gray-600">Exclusives</div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-purple-600 mb-1">
                  {casino.gameLibrary.liveTables ?? '100+'}
                </div>
                <div className="text-xs text-gray-600">Live Tables</div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-purple-600 mb-1">{casino.games}</div>
                <div className="text-xs text-gray-600">Catalog Size</div>
              </div>
            </div>
          </div>
        )}

        {/* Licenses & Payment Methods */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mt-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-shield-check-line text-green-600"></i>
              Licenses
            </h4>
            <div className="flex flex-wrap gap-3">
              {casino.licenses?.map((license, index) => (
                licenseLogoMap[license] ? (
                  <div
                    key={index}
                    className="h-12 bg-white border border-gray-200 rounded-lg px-3 flex items-center justify-center"
                    title={license}
                    aria-label={license}
                  >
                    <img
                      src={licenseLogoMap[license]}
                      alt={license}
                      className="h-8 w-auto object-contain"
                    />
                  </div>
                ) : (
                  <span
                    key={index}
                    className="px-3 py-1 bg-green-50 text-green-700 text-xs font-semibold rounded-full whitespace-nowrap"
                  >
                    {license}
                  </span>
                )
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-bank-card-line text-blue-600"></i>
              Payment Methods
            </h4>
            <div className="flex flex-wrap gap-3">
              {casino.paymentMethods?.map((method, index) => (
                paymentMethodLogoMap[method] ? (
                  <div
                    key={index}
                    className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center p-2"
                    title={method}
                    aria-label={method}
                  >
                    <img
                      src={paymentMethodLogoMap[method]}
                      alt={method}
                      className="w-full h-full object-contain"
                    />
                  </div>
                ) : (
                  <span
                    key={index}
                    className="px-3 py-1 bg-blue-50 text-blue-700 text-xs font-semibold rounded-full whitespace-nowrap"
                  >
                    {method}
                  </span>
                )
              ))}
            </div>
          </div>
        </div>

        {/* Software Providers */}
        {casino.softwareProviders && casino.softwareProviders.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 mt-6">
            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-code-box-line text-purple-600"></i>
              Software Providers
            </h4>
            <div className="flex flex-wrap gap-2">
              {casino.softwareProviders.map((provider, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-purple-50 text-purple-700 text-xs font-semibold rounded-full whitespace-nowrap"
                >
                  {provider}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Editor Note */}
        {casino.editorNote && (
          <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-lg p-4 sm:p-6 mt-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-orange-500 text-white rounded-full flex-shrink-0">
                <i className="ri-user-star-line text-lg"></i>
              </div>
              <div>
                <h4 className="text-sm font-bold text-gray-900 mb-2">Editor's Note</h4>
                <p className="text-sm text-gray-700 italic">&ldquo;{casino.editorNote}&rdquo;</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}